import { useEffect, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  type RevenueSnapshotResponse,
  useReportingRevenueSnapshot,
} from '~entities/payment/api/reporting-revenue-snapshot';

import { useGetCurrency } from '~shared/api/currency-api';
import { cn } from '~shared/shadcn/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~shared/shadcn/ui/tabs';

export const RevenueSnapshot = () => {
  const [mounted, setMounted] = useState(false);
  const { data: currencyDetails } = useGetCurrency();
  const currencySymbol = currencyDetails?.symbol ?? '$';

  // Fetch revenue snapshot data for each timeframe
  const dailyQuery = useReportingRevenueSnapshot({ timeframe: 'daily' });
  const weeklyQuery = useReportingRevenueSnapshot({ timeframe: 'weekly' });
  const monthlyQuery = useReportingRevenueSnapshot({ timeframe: 'monthly' });

  // Helper to get metric data from API response
  const getMetric = (
    query: {
      isLoading: boolean;
      isError: boolean;
      data?: RevenueSnapshotResponse;
    },
    timeframe: 'daily' | 'weekly' | 'monthly',
    symbol: string,
  ) => {
    if (query.isLoading)
      return { value: 'Loading...', change: '', isPositive: true };
    if (query.isError || !query.data)
      return { value: 'N/A', change: '', isPositive: true };
    const snap = query.data.revenueSnapshots.find(
      (s) => s.timeframe === timeframe,
    );
    return {
      value: `${symbol}${snap?.totalRevenue.toLocaleString() ?? '0'}`,
      change: `${snap?.percentageChange && snap.percentageChange > 0 ? '+' : ''}${snap?.percentageChange ?? 0}%`,
      isPositive: (snap?.percentageChange ?? 0) >= 0,
    };
  };

  // Helper to get chart data from API response
  const getChartData = (query: {
    isLoading: boolean;
    data?: RevenueSnapshotResponse;
  }) => {
    if (query.isLoading || !query.data) return [];
    return query.data.dataForChart.map((item) => ({
      name: item.label,
      value: item.revenue,
    }));
  };

  const dailyMetric = getMetric(dailyQuery, 'daily', currencySymbol);
  const weeklyMetric = getMetric(weeklyQuery, 'weekly', currencySymbol);
  const monthlyMetric = getMetric(monthlyQuery, 'monthly', currencySymbol);

  const dailyChartData = getChartData(dailyQuery);
  const weeklyChartData = getChartData(weeklyQuery);
  const monthlyChartData = getChartData(monthlyQuery);

  // This ensures animations only run on client-side
  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className='space-y-6'>
      <h2 className='text-2xl font-semibold'>Revenue Snapshot</h2>
      <div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
        <MetricCard
          title='Total Revenue Today'
          value={dailyMetric.value}
          change={dailyMetric.change}
          isPositive={dailyMetric.isPositive}
          animate={mounted}
        />
        <MetricCard
          title='Total Revenue This Week'
          value={weeklyMetric.value}
          change={weeklyMetric.change}
          isPositive={weeklyMetric.isPositive}
          animate={mounted}
        />
        <MetricCard
          title='Total Revenue This Month'
          value={monthlyMetric.value}
          change={monthlyMetric.change}
          isPositive={monthlyMetric.isPositive}
          animate={mounted}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Revenue by Time Frame</CardTitle>
          <CardDescription>Revenue trends over time</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue='daily' className='space-y-4'>
            <TabsList>
              <TabsTrigger value='daily'>Daily</TabsTrigger>
              <TabsTrigger value='weekly'>Weekly</TabsTrigger>
              <TabsTrigger value='monthly'>Monthly</TabsTrigger>
            </TabsList>
            <TabsContent value='daily' className='pt-2'>
              <RevenueByTimeChart
                data={dailyChartData}
                currencySymbol={currencySymbol}
              />
            </TabsContent>
            <TabsContent value='weekly' className='pt-2'>
              <RevenueByTimeChart
                data={weeklyChartData}
                currencySymbol={currencySymbol}
              />
            </TabsContent>
            <TabsContent value='monthly' className='pt-2'>
              <RevenueByTimeChart
                data={monthlyChartData}
                currencySymbol={currencySymbol}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

function MetricCard({
  title,
  value,
  change,
  isPositive,
  animate,
}: {
  title: string;
  value: string;
  change: string;
  isPositive: boolean;
  animate: boolean;
}) {
  return (
    <Card>
      <CardHeader className='pb-2'>
        <CardTitle className='text-sm font-medium text-muted-foreground'>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            'text-2xl font-bold transition-opacity duration-1000',
            animate ? 'opacity-100' : 'opacity-0',
          )}
        >
          {value}
        </div>
        <div
          className={cn(
            'mt-1 text-xs',
            isPositive ? 'text-green-500' : 'text-red-500',
          )}
        >
          {change} from last period
        </div>
      </CardContent>
    </Card>
  );
}

function RevenueByTimeChart({
  data,
  currencySymbol,
}: {
  data: Array<{ name: string; value: number }>;
  currencySymbol: string;
}) {
  return (
    <div className='h-[300px]'>
      <ResponsiveContainer width='100%' height='100%'>
        <LineChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray='3 3' stroke='#e2e8f0' />
          <XAxis dataKey='name' />
          <YAxis
            tickFormatter={(value: number) =>
              `${currencySymbol}${value.toLocaleString()}`
            }
          />
          <Tooltip
            formatter={(value: number) => [
              `${currencySymbol}${value.toLocaleString()}`,
              'Revenue',
            ]}
          />
          <Line
            type='monotone'
            dataKey='value'
            stroke='#6366f1'
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
